import { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Pencil, ShieldCheck, CircleCheck, CircleAlert, CircleDot, Info, X, Plus, ChevronRight, Target, Users, CalendarClock, Lightbulb, BookOpen, Palette, Upload, FileText, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "../components/ui/accordion";

const PP_FULL = ["directeur", "directeur_adjoint", "directeur_vie_sociale", "coordinateur_vie_sociale",
  "kine", "logopede", "ergo", "animateur", "educateur", "educateur_specialise", "psychomotricien",
  "psychologue", "dieteticien", "referent_demence", "infirmier", "aide_soignant", "assistant_social",
  "medecin", "responsable_unite", "autre_pro"];
const COLOR = {
  red: { bg: "bg-red-500/15", tx: "text-red-300", dot: "#ef4444" },
  orange: { bg: "bg-amber-500/15", tx: "text-amber-300", dot: "#f59e0b" },
  lightgreen: { bg: "bg-lime-500/15", tx: "text-lime-300", dot: "#84cc16" },
  green: { bg: "bg-emerald-500/15", tx: "text-emerald-300", dot: "#10b981" },
  neutral: { bg: "bg-white/[0.06]", tx: "text-slate-300", dot: "#94a3b8" },
};

export default function SuiviPP() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canEdit = PP_FULL.includes(user?.role);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const emptyAdd = { first_name: "", last_name: "", room: "", entry_date: "", referent1: "", referent2: "", first_validation_date: "", last_update_date: "", next_update_date: "", next_meeting_date: "", attestation_signed: false, attestation_file_id: null, attestation_file_name: null };
  const [addForm, setAddForm] = useState(emptyAdd);
  const [delTarget, setDelTarget] = useState(null);

  const deleteResident = async () => {
    if (!delTarget) return;
    setBusy(true);
    try {
      await api.delete(`/pp/residents/${delTarget.id}`);
      toast.success(t("pp.residentDeleted"));
      setDelTarget(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const filter = params.get("filter") || "";

  const filteredRows = useMemo(() => {
    if (!filter) return rows;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const byColor = { en_retard: "red", a_actualiser: "orange", mois_prochain: "lightgreen", a_jour: "green" };
    if (filter === "reunions") return rows.filter((r) => r.next_meeting_date && new Date(r.next_meeting_date) >= today);
    if (byColor[filter]) return rows.filter((r) => r.color === byColor[filter]);
    return rows;
  }, [rows, filter]);

  const clearFilter = () => { params.delete("filter"); setParams(params, { replace: true }); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/pp/tracking"); setRows(data.rows || []); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveRecord = async () => {
    setBusy(true);
    try {
      await api.put(`/pp/records/${edit.id}`, {
        resident_id: edit.id, referent1: edit.referent1 || "", referent2: edit.referent2 || "",
        first_validation_date: edit.first_validation_date || null, last_update_date: edit.last_update_date || null,
        next_update_date: edit.next_update_date || null, next_meeting_date: edit.next_meeting_date || null,
        attestation_signed: !!edit.attestation_signed, attestation_file_id: edit.attestation_file_id || null,
        attestation_file_name: edit.attestation_file_name || null, comments: edit.comments || "",
      });
      toast.success(t("pp.saved")); setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  const addResident = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/pp/residents", {
        first_name: "", last_name: "",
        room: addForm.room || "", entry_date: addForm.entry_date || null, status: "present",
      });
      const rid = data?.id || data?.resident?.id;
      const hasRecord = addForm.referent1 || addForm.referent2 || addForm.first_validation_date || addForm.last_update_date || addForm.next_update_date || addForm.next_meeting_date || addForm.attestation_signed;
      if (rid && hasRecord) {
        await api.put(`/pp/records/${rid}`, {
          resident_id: rid, referent1: addForm.referent1 || "", referent2: addForm.referent2 || "",
          first_validation_date: addForm.first_validation_date || null, last_update_date: addForm.last_update_date || null,
          next_update_date: addForm.next_update_date || null, next_meeting_date: addForm.next_meeting_date || null,
          attestation_signed: !!addForm.attestation_signed, attestation_file_id: addForm.attestation_file_id || null,
          attestation_file_name: addForm.attestation_file_name || null, comments: "",
        });
      }
      toast.success(t("pp.residentAdded")); setAddOpen(false);
      setAddForm(emptyAdd); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setBusy(false);
  };

  const statusLabel = (r) => t(`pp.status.${r.label_key}`, { defaultValue: r.label_key });
  const fmtD = (d) => (d ? new Date(d).toLocaleDateString() : "—");

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="pp-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-100">{t("pp.title")}</h1>
          <p className="text-sm text-slate-400">{t("pp.subtitle")}</p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button onClick={() => setAddOpen(true)} data-testid="pp-add-resident-btn" className="btn-teal-wave font-bold rounded-full px-5 border-0 hover:brightness-105"><Plus className="h-4 w-4 mr-1" />{t("pp.addResident")}</Button>
          </div>
        )}
      </div>

      {/* Légende code couleur */}
      <div className="flex flex-wrap gap-3 mb-4 text-xs">
        {[["red", "en_retard"], ["orange", "a_actualiser"], ["lightgreen", "mois_prochain"], ["green", "a_jour"]].map(([c, k]) => (
          <span key={c} className="inline-flex items-center gap-1.5 text-slate-300"><span className="h-3 w-3 rounded-full" style={{ background: COLOR[c].dot }} />{t(`pp.legend.${k}`)}</span>
        ))}
      </div>

      {/* Bannière de filtre actif (depuis les KPI du dashboard Vie sociale) */}
      {filter && (
        <div className="flex items-center gap-2 mb-4" data-testid="pp-filter-banner">
          <span className="inline-flex items-center gap-2 rounded-full bg-teal-500/15 ring-1 ring-teal-500/30 text-teal-200 px-3 py-1.5 text-xs font-semibold">
            {t("pp.filteredBy")} : {filter === "reunions" ? t("pp.kpi.reunions") : t(`pp.repLabel.${filter}`, { defaultValue: filter })} ({filteredRows.length})
            <button onClick={clearFilter} data-testid="pp-clear-filter" className="hover:text-white"><X className="h-3.5 w-3.5" /></button>
          </span>
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
        <table className="w-full text-sm" data-testid="pp-table">
          <thead className="text-slate-400 border-b border-white/10">
            <tr className="text-left">
              {["habitant", "room", "entry", "ref1", "ref2", "validation", "lastUpdate", "nextUpdate", "meeting", "attestation", "objectives", "statut", ""].map((h, i) => (
                <th key={i} className="px-3 py-2 font-semibold whitespace-nowrap">{h ? t(`pp.col.${h}`, { defaultValue: h }) : ""}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} className="px-3 py-10 text-center text-slate-500">…</td></tr>
            ) : filteredRows.length === 0 ? (
              <tr><td colSpan={13} className="px-3 py-10 text-center text-slate-500" data-testid="pp-empty">{t("pp.empty")}</td></tr>
            ) : filteredRows.map((r) => {
              const col = COLOR[r.color] || COLOR.neutral;
              return (
                <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.03]" data-testid={`pp-row-${r.id}`}>
                  <td className="px-3 py-2 font-semibold text-slate-100 whitespace-nowrap">
                    <button onClick={() => navigate(`/suivi-pp/${r.id}`)} data-testid={`pp-open-${r.id}`} className="hover:text-teal-300 transition">{r.full_name}</button>
                  </td>
                  <td className="px-3 py-2 text-slate-300">{r.room || "—"}</td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtD(r.entry_date)}</td>
                  <td className="px-3 py-2 text-slate-300">{r.referent1 || "—"}</td>
                  <td className="px-3 py-2 text-slate-300">{r.referent2 || "—"}</td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtD(r.first_validation_date)}</td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtD(r.last_update_date)}</td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtD(r.due_date || r.next_update_date)}</td>
                  <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{fmtD(r.next_meeting_date)}</td>
                  <td className="px-3 py-2 text-center">{r.attestation_signed ? (r.attestation_file_id ? <a href={`${process.env.REACT_APP_BACKEND_URL}/api/pp/attestation/${r.attestation_file_id}/file`} target="_blank" rel="noreferrer" data-testid={`pp-attest-link-${r.id}`} className="inline-flex items-center gap-1 text-emerald-400 hover:text-emerald-300" title={r.attestation_file_name || "PDF"}><ShieldCheck className="h-4 w-4" /><FileText className="h-3.5 w-3.5" /></a> : <ShieldCheck className="h-4 w-4 text-emerald-400 inline" />) : <span className="text-slate-600">—</span>}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {(r.goals_total || 0) > 0 ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-slate-300" data-testid={`pp-goals-${r.id}`}>
                        <Target className="h-3.5 w-3.5 text-teal-400" />{r.goals_done || 0}/{r.goals_total}
                      </span>
                    ) : <span className="text-slate-600 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${col.bg} ${col.tx}`}>
                      <span className="h-2 w-2 rounded-full" style={{ background: col.dot }} />{statusLabel(r)}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {canEdit && <button onClick={() => setEdit({ id: r.id, name: r.full_name, referent1: r.referent1, referent2: r.referent2, first_validation_date: r.first_validation_date?.slice(0,10), last_update_date: r.last_update_date?.slice(0,10), next_update_date: r.next_update_date?.slice(0,10), next_meeting_date: r.next_meeting_date?.slice(0,10), attestation_signed: r.attestation_signed, attestation_file_id: r.attestation_file_id, attestation_file_name: r.attestation_file_name, comments: r.comments })} data-testid={`pp-edit-${r.id}`} className="text-slate-400 hover:text-sky-300 mr-1"><Pencil className="h-4 w-4" /></button>}
                    {canEdit && <button onClick={() => setDelTarget({ id: r.id, name: r.full_name })} data-testid={`pp-del-${r.id}`} title={t("pp.deleteResident")} className="text-slate-400 hover:text-red-400 mr-1"><Trash2 className="h-4 w-4 inline" /></button>}
                    <button onClick={() => navigate(`/suivi-pp/${r.id}`)} data-testid={`pp-sheet-${r.id}`} className="text-slate-400 hover:text-teal-300" title={t("pp.openSheet")}><ChevronRight className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PpGuide t={t} colorMap={COLOR} />

      <Dialog open={!!delTarget} onOpenChange={(o) => { if (!o) setDelTarget(null); }}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100 max-w-md" data-testid="pp-del-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-300"><Trash2 className="h-5 w-5" />{t("pp.deleteResident")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-300">{t("pp.deleteConfirm", { name: delTarget?.name })}</p>
          <p className="text-xs text-slate-500">{t("pp.deleteWarning")}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelTarget(null)} data-testid="pp-del-cancel" className="border-white/15 bg-white/5 text-slate-100">{t("pp.cancel")}</Button>
            <Button onClick={deleteResident} disabled={busy} data-testid="pp-del-confirm" className="bg-red-500 hover:bg-red-400 text-white font-semibold">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("pp.deleteResident")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(o) => { if (!o) setEdit(null); }}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100" data-testid="pp-edit-dialog">
          <DialogHeader><DialogTitle>{t("pp.editTitle")} — {edit?.name}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("pp.col.ref1")}><Input value={edit.referent1 || ""} onChange={(e) => setEdit({ ...edit, referent1: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-f-ref1" /></Field>
              <Field label={t("pp.col.ref2")}><Input value={edit.referent2 || ""} onChange={(e) => setEdit({ ...edit, referent2: e.target.value })} className="bg-white/[0.04] border-white/10" /></Field>
              <Field label={t("pp.col.validation")}><Input type="date" value={edit.first_validation_date || ""} onChange={(e) => setEdit({ ...edit, first_validation_date: e.target.value })} className="bg-white/[0.04] border-white/10" /></Field>
              <Field label={t("pp.col.lastUpdate")}><Input type="date" value={edit.last_update_date || ""} onChange={(e) => setEdit({ ...edit, last_update_date: e.target.value })} className="bg-white/[0.04] border-white/10" /></Field>
              <Field label={t("pp.col.nextUpdate")}><Input type="date" value={edit.next_update_date || ""} onChange={(e) => setEdit({ ...edit, next_update_date: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-f-next" /></Field>
              <Field label={t("pp.col.meeting")}><Input type="date" value={edit.next_meeting_date || ""} onChange={(e) => setEdit({ ...edit, next_meeting_date: e.target.value })} className="bg-white/[0.04] border-white/10" /></Field>
              <AttestationField t={t} signed={edit.attestation_signed} fileId={edit.attestation_file_id} fileName={edit.attestation_file_name} onChange={(p) => setEdit((s) => ({ ...s, ...p }))} testid="pp-edit-attest" />
              <Field label={t("pp.col.comments")} full><Input value={edit.comments || ""} onChange={(e) => setEdit({ ...edit, comments: e.target.value })} className="bg-white/[0.04] border-white/10" /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEdit(null)}>{t("pp.cancel")}</Button>
            <Button onClick={saveRecord} disabled={busy} data-testid="pp-save" className="bg-sky-500 hover:bg-sky-400 text-slate-900 font-semibold">{t("pp.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ajout manuel d'un habitant */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100" data-testid="pp-add-dialog">
          <DialogHeader><DialogTitle>{t("pp.addResidentTitle")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("pp.fFirst")}><Input value="" readOnly disabled placeholder="—" className="bg-white/[0.02] border-white/10 opacity-60 cursor-not-allowed" data-testid="pp-add-first" /></Field>
            <Field label={t("pp.fLast")}><Input value="" readOnly disabled placeholder="—" className="bg-white/[0.02] border-white/10 opacity-60 cursor-not-allowed" data-testid="pp-add-last" /></Field>
            <p className="sm:col-span-2 text-[11px] text-amber-300/80 -mt-1">{t("pp.rgpdNameHint")}</p>
            <Field label={t("pp.fRoom")}><Input value={addForm.room} onChange={(e) => setAddForm({ ...addForm, room: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-add-room" /></Field>
            <Field label={t("pp.fEntry")}><Input type="date" value={addForm.entry_date} onChange={(e) => setAddForm({ ...addForm, entry_date: e.target.value })} className="bg-white/[0.04] border-white/10" /></Field>
            <Field label={t("pp.col.ref1")}><Input value={addForm.referent1} onChange={(e) => setAddForm({ ...addForm, referent1: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-add-ref1" /></Field>
            <Field label={t("pp.col.ref2")}><Input value={addForm.referent2} onChange={(e) => setAddForm({ ...addForm, referent2: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-add-ref2" /></Field>
            <Field label={t("pp.col.validation")}><Input type="date" value={addForm.first_validation_date} onChange={(e) => setAddForm({ ...addForm, first_validation_date: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-add-validation" /></Field>
            <Field label={t("pp.col.lastUpdate")}><Input type="date" value={addForm.last_update_date} onChange={(e) => setAddForm({ ...addForm, last_update_date: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-add-lastupdate" /></Field>
            <Field label={t("pp.col.nextUpdate")}><Input type="date" value={addForm.next_update_date} onChange={(e) => setAddForm({ ...addForm, next_update_date: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-add-nextupdate" /></Field>
            <Field label={t("pp.col.meeting")}><Input type="date" value={addForm.next_meeting_date} onChange={(e) => setAddForm({ ...addForm, next_meeting_date: e.target.value })} className="bg-white/[0.04] border-white/10" data-testid="pp-add-meeting" /></Field>
            <AttestationField t={t} signed={addForm.attestation_signed} fileId={addForm.attestation_file_id} fileName={addForm.attestation_file_name} onChange={(p) => setAddForm((s) => ({ ...s, ...p }))} testid="pp-add-attest" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)}>{t("pp.cancel")}</Button>
            <Button onClick={addResident} disabled={busy} data-testid="pp-add-save" className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold">{t("pp.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, full }) {
  return <div className={full ? "col-span-2" : ""}><label className="text-xs font-semibold text-slate-400 block mb-1">{label}</label>{children}</div>;
}

const readAsDataURL = (file) => new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });

function AttestationField({ t, signed, fileId, fileName, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.type !== "application/pdf") { toast.error(t("pp.attestPdfOnly")); return; }
    setBusy(true);
    try {
      const data_url = await readAsDataURL(f);
      const { data } = await api.post("/pp/attestation", { data_url, file_name: f.name });
      onChange({ attestation_signed: true, attestation_file_id: data.file_id, attestation_file_name: data.file_name });
      toast.success(t("pp.attestUploaded"));
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    setBusy(false);
  };
  const base = process.env.REACT_APP_BACKEND_URL;
  return (
    <div className="col-span-2 rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid={testid}>
      <label className="flex items-center gap-2 text-sm font-semibold text-slate-200 cursor-pointer">
        <input type="checkbox" checked={!!signed} data-testid={`${testid}-check`}
          onChange={(e) => onChange(e.target.checked ? { attestation_signed: true } : { attestation_signed: false, attestation_file_id: null, attestation_file_name: null })} />
        {t("pp.attestSigned")}
      </label>
      {signed && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-teal-300 hover:text-teal-200 rounded-md border border-teal-500/30 bg-teal-500/10 px-2.5 py-1.5">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {fileId ? t("pp.attestReplace") : t("pp.attestAttach")}
            <input type="file" accept="application/pdf" className="hidden" onChange={onFile} data-testid={`${testid}-file`} />
          </label>
          {fileId && !busy && (
            <a href={`${base}/api/pp/attestation/${fileId}/file`} target="_blank" rel="noreferrer" data-testid={`${testid}-link`}
              className="text-xs text-slate-300 hover:text-teal-300 underline inline-flex items-center gap-1"><FileText className="h-3.5 w-3.5" />{fileName || "attestation.pdf"}</a>
          )}
          {fileId && !busy && (
            <button type="button" onClick={() => onChange({ attestation_file_id: null, attestation_file_name: null })} className="text-slate-400 hover:text-red-400" title={t("pp.cancel")}><X className="h-3.5 w-3.5" /></button>
          )}
        </div>
      )}
    </div>
  );
}

const GUIDE_ITEMS = [
  { key: "who", icon: Users, color: "#0ea5e9" },
  { key: "when", icon: CalendarClock, color: "#f59e0b" },
  { key: "why", icon: Lightbulb, color: "#a855f7" },
  { key: "how", icon: BookOpen, color: "#10b981" },
];
const GUIDE_COLORS = [
  { key: "en_retard", c: "red" },
  { key: "a_actualiser", c: "orange" },
  { key: "mois_prochain", c: "lightgreen" },
  { key: "a_jour", c: "green" },
];

function PpGuide({ t, colorMap }) {
  return (
    <div className="mt-8" data-testid="pp-guide">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-teal-500/15 ring-1 ring-teal-500/30"><BookOpen className="h-5 w-5 text-teal-300" /></span>
        <div>
          <h2 className="text-lg font-bold text-slate-100">{t("pp.guide.title")}</h2>
          <p className="text-xs text-slate-400">{t("pp.guide.subtitle")}</p>
        </div>
      </div>
      <Accordion type="single" collapsible className="rounded-2xl border border-white/10 bg-white/[0.02] px-4 divide-y divide-white/5">
        {GUIDE_ITEMS.map(({ key, icon: Icon, color }) => (
          <AccordionItem key={key} value={key} className="border-0">
            <AccordionTrigger className="hover:no-underline py-4" data-testid={`pp-guide-${key}`}>
              <span className="flex items-center gap-3 text-left">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${color}22` }}><Icon className="h-4 w-4" style={{ color }} /></span>
                <span className="font-semibold text-slate-100">{t(`pp.guide.${key}.q`)}</span>
              </span>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-slate-300 leading-relaxed pl-11 pb-4 whitespace-pre-line">
              {t(`pp.guide.${key}.a`)}
            </AccordionContent>
          </AccordionItem>
        ))}
        <AccordionItem value="colors" className="border-0">
          <AccordionTrigger className="hover:no-underline py-4" data-testid="pp-guide-colors">
            <span className="flex items-center gap-3 text-left">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-teal-500/15"><Palette className="h-4 w-4 text-teal-300" /></span>
              <span className="font-semibold text-slate-100">{t("pp.guide.colors.q")}</span>
            </span>
          </AccordionTrigger>
          <AccordionContent className="pl-11 pb-4">
            <p className="text-sm text-slate-400 mb-3">{t("pp.guide.colors.intro")}</p>
            <div className="space-y-2">
              {GUIDE_COLORS.map(({ key, c }) => (
                <div key={key} className="flex items-center gap-3" data-testid={`pp-guide-color-${key}`}>
                  <span className="h-3.5 w-3.5 rounded-full shrink-0 ring-2 ring-white/10" style={{ background: (colorMap[c] || {}).dot }} />
                  <span className="text-sm font-semibold text-slate-200 min-w-[130px]">{t(`pp.legend.${key}`)}</span>
                  <span className="text-sm text-slate-400">{t(`pp.guide.colors.${key}`)}</span>
                </div>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
